import React, { useMemo, useState, useEffect } from "react";
import { Whisper, Tooltip } from "rsuite";
import { withRouter } from "react-router-dom";
import { connect } from "react-redux";

import { withTranslation } from "hoc/WithTranslation";
import usePrevious from "hooks/usePrevious";
import { renderMoney } from "utils";
import { BaseToggle } from "components/base/BaseToggle";
import Page from "components/base/Page";
import TrafficStat from "./TrafficStat/TrafficStatContainer";
import CDR from "./CDR/CDRContainer";
import m from "definedMessages";


const Traffic = ({
    service,
    history,
    location,
    cdr_list,
    resetRecaptcha,
    executeRecaptcha,
    formatMessage
}) => {

    const searchParams = new URLSearchParams(window.location.search);
    const typeFromUrl = searchParams.get("type");

    const typesTab = {
        traffic_reports: true,
        cdr: false,
        mdr: false
    };

    const [typeTrafficCDR, setTypeTrafficCDR] = useState(typesTab[typeFromUrl] || false);
    const prevTypeTrafficCDR = usePrevious(typeTrafficCDR);

    const todayDateStart = new Date(new Date().setHours(0, 0, 0, 0));
    const todayDateEnd = new Date(new Date().setHours(23, 59, 59, 999));

    const [dateTimeState, setDateTimeState] = useState([todayDateStart, todayDateEnd]);

    const voiceCdrColumns = useMemo(() => {
        return [
            {
                id: "datetime",
                label: formatMessage(m.datetime),
                dataKey: "datetime",
                align: "center",
                width: 180
            },
            {
                id: "a_number",
                label: formatMessage(m.aNumber),
                dataKey: "a_number",
                width: 140,
                align: "left"
            },
            {
                id: "b_number",
                label: formatMessage(m.bNumber),
                dataKey: "b_number",
                width: 140,
                align: "left"
            },
            {
                id: "range",
                label: formatMessage(m.range),
                dataKey: "range",
                flexGrow: 2,
                minWidth: 250,
                align: "left"
            },
            {
                id: "rate_dialer",
                label: formatMessage(m.rate),
                // dataKey: "rate_dialer",
                value: ({ rate_dialer, cur_name, summary }) => {
                    if (summary) {
                        return rate_dialer ? renderMoney(rate_dialer, cur_name) : "";
                    }

                    return renderMoney(rate_dialer, cur_name);
                },
                width: 100,
                align: "right"
            },
            {
                id: "cost_dialer",
                label: formatMessage(m.cost),
                // dataKey: "cost_dialer",
                value: ({ cost_dialer, cur_name, summary }) => {
                    if (summary) {
                        return cost_dialer ? renderMoney(cost_dialer, cur_name) : "";
                    }

                    return renderMoney(cost_dialer, cur_name);
                },
                width: 110,
                align: "right"
            },
            {
                id: "term",
                label: formatMessage(m.termPoint),
                value: (item) => {
                    if (item.id === "summaries")
                        return "";

                    const { ip, port, tprefix_dialer } = item;
                    return `${ip}:${port} ${tprefix_dialer}`;
                },
                width: 170,
                align: "left"
            },
            {
                id: "duration",
                label: formatMessage(m.duration),
                value: (item) => {
                    return item.id === "summaries"
                        ? <span title={item.duration}>{item.duration}</span>
                        : item.duration;
                },
                // dataKey: "duration",
                width: 90,
                align: "left"
            },
            {
                id: "sip",
                label: formatMessage(m.sipCause),
                dataKey: "sip_cause",
                // flexGrow: 2,
                width: 75,
                align: "right"
            },
        ]
    }, []);

    const smsCdrColumns = useMemo(() => {
        return [
            {
                id: "datetime",
                label: formatMessage(m.datetime),
                dataKey: "datetime",
                align: "center",
                width: 150
            },
            {
                id: "a_number",
                label: formatMessage(m.senderId),
                dataKey: "senderid",
                width: 120,
                align: "left"
            },
            {
                id: "b_number",
                label: formatMessage(m.phone),
                dataKey: "phone",
                width: 140,
                align: "left"
            },
            {
                id: "range",
                label: formatMessage(m.range),
                dataKey: "range_name",
                flexGrow: 2,
                minWidth: 250,
                align: "left"
            },
            {
                id: "rate_dialer",
                label: formatMessage(m.rate),
                // dataKey: "rate_dialer",
                value: ({ dialer_rate, dialer_cur_name, summary }) => {
                    if (summary) {
                        return dialer_rate ? `${dialer_rate.toFixed(2)} ${dialer_cur_name}` : "";
                    }

                    return renderMoney(dialer_rate, dialer_cur_name);
                },
                width: 100,
                align: "right"
            },
            {
                id: "message",
                label: formatMessage(m.message),
                // dataKey: "message",
                value: ({ message }) => {
                    return (
                        <Whisper
                            enterable
                            placement="bottomStart"
                            speaker={
                                <Tooltip>{message}</Tooltip>
                            }
                        >
                            <span>{message}</span>
                        </Whisper>
                    )
                },
                flexGrow: 3,
                minWidth: 300,
                align: "left"
            }
        ]
    }, []);

    const voiceTrafficColumns = useMemo(() => {
        return [
            {
                id: "month",
                label: formatMessage(m.month),
                dataKey: "month",
                width: 100,
                align: "center"
            },
            {
                id: "day",
                label: formatMessage(m.day),
                dataKey: "day",
                width: 100,
                align: "center"
            },
            {
                id: "hour",
                label: formatMessage(m.hour),
                dataKey: "hour",
                width: 110,
                align: "center"
            },
            {
                id: "a_number",
                label: formatMessage(m.aNumber),
                dataKey: "a_number",
                width: 140,
                align: "left"
            },
            {
                id: "b_number",
                label: formatMessage(m.bNumber),
                dataKey: "b_number",
                width: 140,
                align: "left"
            },
            {
                id: "range",
                label: formatMessage(m.ranges),
                dataKey: "range",
                flexGrow: 2,
                minWidth: 250,
                align: "left"
            },
            {
                id: "b_destination",
                label: formatMessage(m.destination),
                dataKey: "b_destination",
                flexGrow: 2,
                minWidth: 250,
                align: "left"
            },
            {
                id: "duration",
                label: formatMessage(m.duration),
                dataKey: "sum_duration",
                value: (item) => {
                    return item.id === "summaries"
                        ? <span title={item.sum_duration}>{item.sum_duration}</span>
                        : item.sum_duration
                },
                width: 80,
                align: "left"
            },
            {
                id: "call",
                label: formatMessage(m.calls),
                dataKey: "count_cost_dialer",
                value: ({ count_cost_dialer }) => {
                    return <span title={count_cost_dialer}>{count_cost_dialer}</span>;
                },
                width: 70,
                align: "left"
            },
            {
                id: "cost",
                label: formatMessage(m.cost),
                dataKey: "sum_cost_dialer",
                value: ({ sum_cost_dialer, cur_name }) => {
                    let value = sum_cost_dialer;

                    if (value && cur_name) {
                        return `${value} ${cur_name}`;
                    } else if (value && !cur_name) {
                        return value;
                    }
                    return "";
                },
                width: 110,
                align: "right"
            },
        ]
    }, []);

    const smsTrafficColumns = useMemo(() => {
        return [
            {
                id: "month",
                label: formatMessage(m.month),
                dataKey: "month",
                width: 100,
                align: "center"
            },
            {
                id: "day",
                label: formatMessage(m.day),
                dataKey: "day",
                width: 100,
                align: "center"
            },
            {
                id: "hour",
                label: formatMessage(m.hour),
                dataKey: "hour",
                width: 110,
                align: "center"
            },
            {
                id: "senderid",
                label: formatMessage(m.senderId),
                dataKey: "senderid",
                width: 120,
                align: "left"
            },
            {
                id: "phone",
                label: formatMessage(m.phone),
                dataKey: "phone",
                width: 140,
                align: "left"
            },
            {
                id: "range",
                label: formatMessage(m.ranges),
                dataKey: "range",
                flexGrow: 2,
                minWidth: 250,
                align: "left"
            },
            {
                id: "destination",
                label: formatMessage(m.destination),
                dataKey: "destination",
                flexGrow: 2,
                minWidth: 250,
                align: "left"
            },
            {
                id: "call",
                label: formatMessage(m.messages),
                dataKey: "count_rate_dialer",
                value: ({ count_rate_dialer }) => {
                    return <span title={count_rate_dialer}>{count_rate_dialer}</span>;
                },
                width: 80,
                align: "left"
            },
            {
                id: "cost",
                label: formatMessage(m.cost),
                dataKey: "sum_rate_dialer",
                value: ({ sum_rate_dialer, cur_name }) => {
                    return renderMoney(sum_rate_dialer, cur_name);
                },
                width: 110,
                align: "right"
            }
        ]
    }, []);


    const cdrColumns = service ? voiceCdrColumns : smsCdrColumns;
    const trafficColumns = service ? voiceTrafficColumns : smsTrafficColumns;

    useEffect(() => {
        if (searchParams.has("type")) {
            searchParams.delete("type");

            history.replace({
                ...location,
                search: ""
            });
        }
    }, [typeTrafficCDR]);

    return (
        <Page>
            <BaseToggle
                key={typeTrafficCDR}
                checkedChildren={formatMessage(m.trafficStat)}
                unCheckedChildren={service ? formatMessage(m.cdr) : formatMessage(m.mdr)}
                // defaultChecked={typeTrafficCDR}
                checked={typeTrafficCDR}
                onChange={value => setTypeTrafficCDR(value)}
            />
            {typeTrafficCDR ? <TrafficStat
                service={service}
                location={location}
                columns={trafficColumns}
                type={typeTrafficCDR}
                prevType={prevTypeTrafficCDR}
                dateTimeState={dateTimeState}
                setDateTimeState={setDateTimeState}
            /> : <CDR
                key={service ? "voice" : "sms"}
                service={service}
                location={location}
                columns={cdrColumns}
                type={typeTrafficCDR}
                prevType={prevTypeTrafficCDR}
                dateTimeState={dateTimeState}
                setDateTimeState={setDateTimeState}
                {...{
                    cdr_list,
                    resetRecaptcha,
                    executeRecaptcha
                }}
            />}
        </Page>
    );
};

const mapState = ({ auth_dialer, client_cdr }) => ({
    service: auth_dialer.serviceDialer,
    cdr_list: client_cdr.cdr_list,
});

export default connect(mapState, {})(withTranslation(withRouter(Traffic)));
